import { createContext, ReactNode, useContext, useMemo, useState, useEffect, useCallback } from 'react';
import { useColorScheme, Platform, PixelRatio } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

const THEME_PREFERENCE_KEY = '@tripvio_theme_preference';

export type ThemePreference = 'light' | 'dark' | 'system';

const baseSpacingUnit = 8;

// Tokenized spacing scale for consistent vertical rhythm
const spacingSteps = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
};

const lightColors = {
  background: '#ffffff',
  backgroundSecondary: '#f5f7f5', // Secondary background for cards, sections
  /** Slightly muted canvas (chips, inactive strips). Alias of backgroundSecondary. */
  backgroundMuted: '#f5f7f5',
  surface: '#ffffff',
  surfaceElevated: '#ffffff', // For elevated modals/dropdowns
  primary: '#132a13',
  primaryContainer: '#e8f5e9', // Light background for primary-colored elements
  onPrimary: '#ffffff',
  onPrimaryContainer: '#132a13',
  accent: '#f5d787',
  onAccent: '#132a13', // Dark text for use on accent colored backgrounds
  text: '#132a13',
  textSecondary: '#4a5a4a', // Improved contrast: changed from #5c6b5c to meet WCAG AA (4.5:1 ratio)
  textTertiary: '#6a7a6a', // Subtle meta text with safe contrast on light backgrounds
  textOnPrimary: '#ffffff', // Text on primary colored elements
  textInverse: '#ffffff', // Text on dark backgrounds
  border: '#c5d5c2', // More visible borders: changed from #d5dbd2
  borderMuted: '#d5e5d2', // Still subtle but visible: changed from #e5ebe2
  success: '#2f6f3f',
  successContainer: '#d4edda',
  onSuccess: '#ffffff',
  onSuccessContainer: '#2f6f3f',
  warning: '#d9912a',
  warningContainer: '#fff3cd',
  onWarning: '#ffffff',
  onWarningContainer: '#d9912a',
  error: '#b23a3a',
  errorContainer: '#f8d7da',
  onError: '#ffffff',
  onErrorContainer: '#b23a3a',
  overlay: 'rgba(19,42,19,0.08)',
  modalOverlay: 'rgba(19,42,19,0.5)', // Dark overlay for modals
  /** Subtle alternate surface (callouts, inline panels). Alias of backgroundSecondary. */
  surfaceVariant: '#f0f3f0',
  /** Informational accents (icons, callout borders). */
  info: '#2563eb',
  /** Card / sheet backgrounds (alias of surface). */
  card: '#ffffff',
  /** Alternate list/table surface (alias of surfaceVariant). */
  surfaceAlt: '#f0f3f0',
};

const darkColors = {
  background: '#000000',
  backgroundSecondary: '#121212', // Secondary background for cards, sections
  backgroundMuted: '#121212',
  surface: '#1e1e1e',
  surfaceElevated: '#2a2a2a', // For elevated modals/dropdowns - slightly lighter
  primary: '#ffffff',
  primaryContainer: '#2d3a2d', // Dark background for primary-colored elements
  onPrimary: '#132a13',
  onPrimaryContainer: '#ffffff',
  accent: '#f5d787',
  onAccent: '#132a13', // Dark text for use on accent colored backgrounds
  text: '#ffffff',
  textSecondary: '#cccccc',
  textTertiary: '#9a9a9a', // Subtle meta text with safe contrast on dark backgrounds
  textOnPrimary: '#132a13', // Text on primary colored elements (dark in dark mode since primary is white)
  textInverse: '#000000', // Text on light backgrounds
  border: '#333333',
  borderMuted: '#252525',
  success: '#4ade80',
  successContainer: '#1a3a1a',
  onSuccess: '#000000',
  onSuccessContainer: '#4ade80',
  warning: '#fbbf24',
  warningContainer: '#3a2a0a',
  onWarning: '#000000',
  onWarningContainer: '#fbbf24',
  error: '#f87171',
  errorContainer: '#3a1a1a',
  onError: '#000000',
  onErrorContainer: '#f87171',
  overlay: 'rgba(0,0,0,0.5)',
  modalOverlay: 'rgba(0,0,0,0.7)', // Darker overlay for modals in dark mode
  surfaceVariant: '#252525',
  info: '#60a5fa',
  card: '#1e1e1e',
  surfaceAlt: '#252525',
};

// Helper to apply dynamic type scaling for iOS accessibility
const getScaledSize = (baseSize: number): number => {
  if (Platform.OS === 'ios') {
    const fontScale = PixelRatio.getFontScale();
    return Math.max(baseSize * fontScale, baseSize); // Don't scale down, only up
  }
  return baseSize;
};

// Base typography sizes (will be scaled at runtime)
// Using consistent font weights: 400 (regular), 500 (medium), 600 (semibold), 700 (bold)
// Scale tuned for clearer hierarchy on small screens
const baseTypography = {
  hero: { fontSize: 32, lineHeight: 40, fontWeight: '700' as const }, // Screen title
  h1: { fontSize: 24, lineHeight: 32, fontWeight: '700' as const }, // Primary section headers
  h2: { fontSize: 20, lineHeight: 28, fontWeight: '700' as const }, // Secondary headers
  h3: { fontSize: 18, lineHeight: 24, fontWeight: '600' as const }, // Tertiary headers / card titles
  title: { fontSize: 16, lineHeight: 24, fontWeight: '600' as const }, // Row titles
  body: { fontSize: 14, lineHeight: 22, fontWeight: '400' as const }, // Body copy
  bodyStrong: { fontSize: 14, lineHeight: 22, fontWeight: '600' as const }, // Emphasized body
  caption: { fontSize: 12, lineHeight: 18, fontWeight: '500' as const }, // Meta text
  label: { fontSize: 12, lineHeight: 16, fontWeight: '600' as const }, // Pills / small labels
  h4: { fontSize: 16, lineHeight: 22, fontWeight: '600' as const },
  /** Alias tokens for copy-heavy screens that use legacy variant names */
  heading: { fontSize: 24, lineHeight: 32, fontWeight: '700' as const },
  subheading: { fontSize: 20, lineHeight: 28, fontWeight: '700' as const },
};

// Get typography with dynamic scaling applied
const getTypography = () => ({
  hero: { ...baseTypography.hero, fontSize: getScaledSize(32), lineHeight: getScaledSize(40) },
  h1: { ...baseTypography.h1, fontSize: getScaledSize(26), lineHeight: getScaledSize(32) },
  h2: { ...baseTypography.h2, fontSize: getScaledSize(22), lineHeight: getScaledSize(28) },
  h3: { ...baseTypography.h3, fontSize: getScaledSize(20), lineHeight: getScaledSize(26) },
  title: { ...baseTypography.title, fontSize: getScaledSize(17), lineHeight: getScaledSize(24) },
  body: { ...baseTypography.body, fontSize: getScaledSize(15), lineHeight: getScaledSize(23) },
  bodyStrong: { ...baseTypography.bodyStrong, fontSize: getScaledSize(15), lineHeight: getScaledSize(23) },
  caption: { ...baseTypography.caption, fontSize: getScaledSize(13), lineHeight: getScaledSize(18) },
  label: { ...baseTypography.label, fontSize: getScaledSize(12), lineHeight: getScaledSize(16) },
  h4: { ...baseTypography.h4, fontSize: getScaledSize(17), lineHeight: getScaledSize(23) },
  heading: { ...baseTypography.heading, fontSize: getScaledSize(26), lineHeight: getScaledSize(32) },
  subheading: { ...baseTypography.subheading, fontSize: getScaledSize(22), lineHeight: getScaledSize(28) },
});

const radii = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 24,
  full: 999,
  pill: 999,
};

// Helper function to create web-compatible shadows
const createShadow = (
  shadowColor: string,
  shadowOpacity: number,
  shadowOffset: { width: number; height: number },
  shadowRadius: number,
  elevation: number
) => {
  if (Platform.OS === 'web') {
    const { width, height } = shadowOffset;
    // Parse rgba color string
    const rgbaMatch = shadowColor.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?\)/);
    let boxShadowColor = shadowColor;
    
    if (rgbaMatch) {
      const r = rgbaMatch[1];
      const g = rgbaMatch[2];
      const b = rgbaMatch[3];
      const a = rgbaMatch[4] ? parseFloat(rgbaMatch[4]) * shadowOpacity : shadowOpacity;
      boxShadowColor = `rgba(${r}, ${g}, ${b}, ${a})`;
    }
    
    return {
      boxShadow: `${width}px ${height}px ${shadowRadius}px ${boxShadowColor}`,
      elevation,
    };
  }
  
  return {
    shadowColor,
    shadowOpacity,
    shadowOffset,
    shadowRadius,
    elevation,
  };
};

// Shadows will be created at runtime
const getShadows = () => ({
  level0: createShadow(
    'rgba(19,42,19,0.08)',
    0.08,
    { width: 0, height: 2 },
    8,
    1
  ),
  level1: createShadow(
    'rgba(19,42,19,0.12)',
    0.12,
    { width: 0, height: 4 },
    12,
    3
  ),
  level2: createShadow(
    'rgba(19,42,19,0.16)',
    0.16,
    { width: 0, height: 8 },
    16,
    6
  ),
  level3: createShadow(
    'rgba(19,42,19,0.20)',
    0.20,
    { width: 0, height: 12 },
    20,
    8
  ),
});

export type TripFlowColors = typeof lightColors;
export type TripFlowTypography = ReturnType<typeof getTypography>;
export type TripFlowTheme = {
  colors: TripFlowColors;
  typography: TripFlowTypography;
  radii: typeof radii;
  shadows: ReturnType<typeof getShadows>;
  spacing: (step?: number) => number;
  spacingSteps: typeof spacingSteps;
  colorScheme: 'light' | 'dark';
};

const createTheme = (scheme: 'light' | 'dark'): TripFlowTheme => {
  const palette = scheme === 'dark' ? darkColors : lightColors;

  return {
    colors: palette,
    typography: getTypography(),
    radii,
    shadows: getShadows(),
    colorScheme: scheme,
    spacing: (step = 1) => step * baseSpacingUnit,
    spacingSteps,
  };
};

/** Default when user has never chosen a preference (AsyncStorage miss). */
const DEFAULT_THEME_PREFERENCE: ThemePreference = 'dark';

const defaultTheme = createTheme('dark');

type ThemeContextValue = {
  theme: TripFlowTheme;
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
};

const ThemeContext = createContext<ThemeContextValue>({
  theme: defaultTheme,
  preference: DEFAULT_THEME_PREFERENCE,
  setPreference: () => {},
});

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const systemColorScheme = useColorScheme();
  const [preference, setPreferenceState] = useState<ThemePreference>(DEFAULT_THEME_PREFERENCE);
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    let cancelled = false;
    AsyncStorage.getItem(THEME_PREFERENCE_KEY)
      .then((stored) => {
        if (cancelled) return;
        if (stored === 'light' || stored === 'dark' || stored === 'system') {
          setPreferenceState(stored);
        }
        setHydrated(true);
      })
      .catch(() => setHydrated(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    AsyncStorage.setItem(THEME_PREFERENCE_KEY, next).catch(() => {});
  }, []);

  const effectiveScheme: 'light' | 'dark' =
    preference === 'system'
      ? systemColorScheme === 'dark'
        ? 'dark'
        : 'light'
      : preference;

  const theme = useMemo(() => createTheme(effectiveScheme), [effectiveScheme]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      preference: hydrated ? preference : DEFAULT_THEME_PREFERENCE,
      setPreference,
    }),
    [theme, preference, hydrated, setPreference],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => useContext(ThemeContext).theme;

export const useThemeSetting = (): {
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  colorScheme: 'light' | 'dark';
} => {
  const { theme, preference, setPreference } = useContext(ThemeContext);
  return {
    preference,
    setPreference,
    colorScheme: theme.colorScheme,
  };
};

export const tokens = {
  colors: { light: lightColors, dark: darkColors },
  typography: baseTypography, // Base typography without scaling
  radii,
  shadows: getShadows(),
  spacingUnit: baseSpacingUnit,
  spacingSteps,
};

